import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import type { ColDef, RowSelectionOptions } from 'ag-grid-community';
import {
  AllCommunityModule,
  colorSchemeLightCold,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef, useState } from 'react';
import type { ActorRefFrom } from 'xstate';
import { EmptyState, LoadingSpinner } from '~/components';
import { FilterBanner, useFilterBanner } from '~/components/agGrid/FilterBanner';
import {
  ACTIONS_COLUMN_BASE,
  ActionsRenderer,
  BASE_GRID_STYLE,
  type ActionDef,
} from '~/components/Table/gridUtil';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { usePrivilegesRef } from '../account/vaultSelectors';
import { withNewRecordPill } from '../newRecords/NewRecordPill';
import { useNewRecordPills } from '../newRecords/useNewRecords';
import { getSharedColumns } from './constants';
import type { privilegesMachine } from './privileges.machine';

/**
 * [MCV-1231] Empty-state overlay for the privileges grid.
 *
 * AgGrid's default overlay shows a terse "No rows to show" with no context.
 * Previously, a provider whose API returned `[]` (legitimately — not yet
 * granted any privileges) saw only column headers and that sparse message,
 * which read like a loading bug. This overlay makes the empty state
 * intentional and guides the user toward the "Add Privilege" flow above.
 */
const PrivilegesNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.privileges}
    title="No privileges yet"
    description="Add your first privilege to get started"
    cyTag={CT.privileges.table.emptyState}
  />
);

const rowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  headerCheckbox: false,
};

ModuleRegistry.registerModules([AllCommunityModule]);
const theme = themeQuartz.withPart(colorSchemeLightCold).withParams(BASE_GRID_STYLE);

const PrivilegesGridInner = ({
  privilegesRef,
  setSelectedIds,
}: {
  privilegesRef: ActorRefFrom<typeof privilegesMachine>;
  setSelectedIds: (ids: number[]) => void;
}) => {
  const [searchText, setSearchText] = useState('');
  const privilegeCrudMachine = useSelector(privilegesRef, (state) => state.context.crudRef);
  const { data: rawPrivileges, loading } = useSelector(
    privilegeCrudMachine,
    (state) => state.context
  );
  const privileges = rawPrivileges;

  const handleDeleteClick = (id: number) => {
    privilegesRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };
  const handleEditClick = (id: number) => {
    privilegeCrudMachine.send({ type: 'ON_EDIT', payload: { id } });
  };

  const [colDefs] = useState<ColDef[]>([
    { field: 'id', hide: true },
    ...getSharedColumns('specialtyName.label'),
    {
      field: 'assignmentDate',
      filter: 'agDateColumnFilter',
    },
    {
      ...ACTIONS_COLUMN_BASE,
      width: 100,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        actions: [
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          { type: 'notes', handler: () => {}, cyTag: '' },
          {
            type: 'edit',
            handler: handleEditClick,
            cyTag: CT.privileges.table.editBtn,
            // MCV-702: pre-set privileges are add-or-delete only; only user-added rows are editable.
            show: (data) => data.userAddedFlag === true,
          },
          { type: 'delete', handler: handleDeleteClick, cyTag: CT.privileges.table.deleteBtn },
        ] satisfies ActionDef[],
      },
    },
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      cellStyle: { display: 'flex', alignItems: 'center' },
      filter: true,
      resizable: true,
      flex: 1,
    };
  }, []);

  const gridRef = useRef<AgGridReact>(null);
  // [MCV-1812] Records a document parse just created carry a "New" pill for this visit.
  const newRecordIds = useNewRecordPills('provider_privileges', privileges);
  const columnDefs = useMemo(
    () => withNewRecordPill(colDefs, newRecordIds),
    [colDefs, newRecordIds]
  );
  const { filtersActive, handleFilterChanged, clearFilters, registerGridApi } = useFilterBanner();
  const clearAllFilters = () => {
    setSearchText('');
    clearFilters();
  };
  const onSelectionChanged = () => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    setSelectedIds(selectedRows.map((row: { id: number }) => row.id));
  };

  return (
    <div className="h-full w-full flex flex-col" data-cy={CT.privileges.table.container}>
      <label className="mb-2 flex items-center gap-2 rounded-md border border-grey-900 px-3 py-2 w-full sm:w-72 shrink-0">
        <MagnifyingGlassIcon className="h-4 w-4 text-grey-500" />
        <input
          aria-label="Search privileges"
          className="w-full bg-transparent text-sm outline-none"
          placeholder="Search privileges…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </label>
      <div className="flex-1 min-h-0 flex flex-col">
        <FilterBanner
          active={filtersActive || searchText.trim().length > 0}
          onClear={clearAllFilters}
        />
        <div className="flex-1 min-h-0">
          <AgGridReact
            theme={theme}
            colResizeDefault="shift"
            enableFilterHandlers={true}
            rowData={privileges}
            loading={loading}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={searchText}
            pagination={true}
            paginationAutoPageSize={true}
            rowSelection={rowSelection}
            onSelectionChanged={onSelectionChanged}
            onFilterChanged={handleFilterChanged}
            onGridReady={(params) => registerGridApi(params.api)}
            loadingOverlayComponent={() => (
              <LoadingSpinner cyTag={CT.components.loadingSpinner.spinner} />
            )}
            noRowsOverlayComponent={PrivilegesNoRowsOverlay}
            ref={gridRef}
          />
        </div>
      </div>
    </div>
  );
};

export const PrivilegesGrid = ({ setSelectedIds }: { setSelectedIds: (ids: number[]) => void }) => {
  const { privilegesRef } = usePrivilegesRef();
  if (!privilegesRef) return null;
  return <PrivilegesGridInner privilegesRef={privilegesRef} setSelectedIds={setSelectedIds} />;
};
